import {
    Add,
    AddCircleOutline,
    AdminPanelSettings,
    Block,
    CheckCircle,
    ExpandLess,
    ExpandMore,
    LockReset,
    Person,
    PersonAdd,
    RemoveCircleOutline,
    Search,
    Shield,
    Storage,
    Visibility,
    VisibilityOff,
} from '@mui/icons-material';
import {
    Autocomplete,
    Box,
    Card,
    CardContent,
    Chip,
    Collapse,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Button,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { COUNTRIES, type Country } from '@/config/countries';
import type { AdminUserView } from '@/domain/entities/user';
import { adminApi, ApiError } from '@/infrastructure/services/api-client';
import { useNotificationStore } from '@/stores/use-notification-store';
import { useThemeStore } from '@/stores/use-theme-store';

function extractDomain(email: string): string {
    return email.split('@')[1]?.toLowerCase() ?? '';
}

const PLAN_IDS = ['mcp-abap', 'mcp-btp'] as const;
const PAGE_SIZE_OPTIONS = [10, 25, 50];

function planLabel(planId: string): string {
    return planId.replace('mcp-', '').toUpperCase();
}

type ConfirmAction = {
    type: 'role' | 'status' | 'grant-plan' | 'revoke-plan';
    user: AdminUserView;
    planId?: string;
};

type CreateUserForm = {
    name: string;
    email: string;
    password: string;
    country: Country | null;
    role: 'user' | 'admin';
};

const emptyCreateForm = (): CreateUserForm => ({
    name: '',
    email: '',
    password: '',
    country: null,
    role: 'user',
});

export function AdminUsersPage() {
    const { t } = useTranslation();
    const { addNotification } = useNotificationStore();
    const { mode } = useThemeStore();

    const [users, setUsers] = useState<AdminUserView[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
    const [confirmAction, setConfirmAction] = useState<ConfirmAction | null>(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [allowedDomains, setAllowedDomains] = useState<Set<string>>(new Set());

    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState<CreateUserForm>(emptyCreateForm);
    const [showCreatePassword, setShowCreatePassword] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);

    const [passwordUser, setPasswordUser] = useState<AdminUserView | null>(null);
    const [newPassword, setNewPassword] = useState('');
    const [showResetPassword, setShowResetPassword] = useState(false);
    const [passwordLoading, setPasswordLoading] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            const [usersResult, domainsResult] = await Promise.all([
                adminApi.listUsers(),
                adminApi.listDomains(),
            ]);
            setUsers(usersResult.users);
            setAllowedDomains(new Set(domainsResult.domains.map((d) => d.domain.toLowerCase())));
        } catch (error) {
            if (error instanceof ApiError) {
                addNotification(error.message, 'error');
            }
        } finally {
            setLoading(false);
        }
    }, [addNotification]);

    useEffect(() => {
        void fetchData();
    }, [fetchData]);

    const filteredUsers = useMemo(() => {
        if (!search.trim()) {
            return users;
        }
        const q = search.toLowerCase();
        return users.filter(
            (u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
        );
    }, [users, search]);

    useEffect(() => {
        setPage(0);
    }, [search, rowsPerPage]);

    const paginatedUsers = useMemo(() => {
        const start = page * rowsPerPage;
        return filteredUsers.slice(start, start + rowsPerPage);
    }, [filteredUsers, page, rowsPerPage]);

    const isCreateValid =
        createForm.name.trim().length >= 2 &&
        createForm.email.includes('@') &&
        createForm.password.length >= 8 &&
        !!createForm.country;

    const handleToggleRole = async (user: AdminUserView) => {
        setActionLoading(true);
        try {
            const result = await adminApi.toggleRole(user.id);
            setUsers((prev) =>
                prev.map((u) => (u.id === result.id ? { ...u, role: result.role } : u)),
            );
            addNotification(t('admin.users.roleUpdated'), 'success');
        } catch (error) {
            if (error instanceof ApiError) {
                addNotification(error.message, 'error');
            }
        } finally {
            setActionLoading(false);
            setConfirmAction(null);
        }
    };

    const handleToggleStatus = async (user: AdminUserView) => {
        setActionLoading(true);
        try {
            const result = await adminApi.toggleStatus(user.id);
            setUsers((prev) =>
                prev.map((u) => (u.id === result.id ? { ...u, status: result.status } : u)),
            );
            addNotification(t('admin.users.statusUpdated'), 'success');
        } catch (error) {
            if (error instanceof ApiError) {
                addNotification(error.message, 'error');
            }
        } finally {
            setActionLoading(false);
            setConfirmAction(null);
        }
    };

    const handleGrantPlan = async (user: AdminUserView, planId: string) => {
        setActionLoading(true);
        try {
            await adminApi.grantPlan(user.id, planId);
            addNotification(t('admin.users.planGranted'), 'success');
            await fetchData();
        } catch (error) {
            if (error instanceof ApiError) {
                addNotification(error.message, 'error');
            }
        } finally {
            setActionLoading(false);
            setConfirmAction(null);
        }
    };

    const handleRevokePlan = async (user: AdminUserView, planId: string) => {
        setActionLoading(true);
        try {
            await adminApi.revokePlan(user.id, planId);
            addNotification(t('admin.users.planRevoked'), 'success');
            await fetchData();
        } catch (error) {
            if (error instanceof ApiError) {
                addNotification(error.message, 'error');
            }
        } finally {
            setActionLoading(false);
            setConfirmAction(null);
        }
    };

    const handleConfirm = async () => {
        if (!confirmAction) {
            return;
        }
        if (confirmAction.type === 'role') {
            await handleToggleRole(confirmAction.user);
        } else if (confirmAction.type === 'status') {
            await handleToggleStatus(confirmAction.user);
        } else if (confirmAction.type === 'grant-plan' && confirmAction.planId) {
            await handleGrantPlan(confirmAction.user, confirmAction.planId);
        } else if (confirmAction.type === 'revoke-plan' && confirmAction.planId) {
            await handleRevokePlan(confirmAction.user, confirmAction.planId);
        }
    };

    const handleCreateUser = async () => {
        if (!createForm.country || !isCreateValid) {
            return;
        }
        setCreateLoading(true);
        try {
            await adminApi.createUser({
                name: createForm.name.trim(),
                email: createForm.email.trim(),
                password: createForm.password,
                country: createForm.country.name,
                role: createForm.role,
            });
            addNotification(t('admin.users.userCreated'), 'success');
            setCreateOpen(false);
            setCreateForm(emptyCreateForm());
            setShowCreatePassword(false);
            await fetchData();
        } catch (error) {
            if (error instanceof ApiError) {
                addNotification(error.message, 'error');
            }
        } finally {
            setCreateLoading(false);
        }
    };

    const handleResetPassword = async () => {
        if (!passwordUser || newPassword.length < 8) {
            return;
        }
        setPasswordLoading(true);
        try {
            await adminApi.resetPassword(passwordUser.id, newPassword);
            addNotification(t('admin.users.passwordUpdated'), 'success');
            setPasswordUser(null);
            setNewPassword('');
            setShowResetPassword(false);
        } catch (error) {
            if (error instanceof ApiError) {
                addNotification(error.message, 'error');
            }
        } finally {
            setPasswordLoading(false);
        }
    };

    const getConfirmMessage = (): string => {
        if (!confirmAction) {
            return '';
        }
        if (confirmAction.type === 'role') {
            return confirmAction.user.role === 'admin'
                ? t('admin.users.confirmDemote')
                : t('admin.users.confirmPromote');
        }
        if (confirmAction.type === 'grant-plan' && confirmAction.planId) {
            return t('admin.users.confirmGrantPlan', {
                plan: planLabel(confirmAction.planId),
                user: confirmAction.user.name,
            });
        }
        if (confirmAction.type === 'revoke-plan' && confirmAction.planId) {
            return t('admin.users.confirmRevokePlan', {
                plan: planLabel(confirmAction.planId),
                user: confirmAction.user.name,
            });
        }
        return confirmAction.user.status === 'active'
            ? t('admin.users.confirmDisable')
            : t('admin.users.confirmEnable');
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    };

    const getActivePlans = (user: AdminUserView): string => {
        const active = user.subscriptions.filter((s) => s.status === 'active');
        if (active.length === 0) {
            return '—';
        }
        return active.map((s) => s.planId.replace('mcp-', '').toUpperCase()).join(', ');
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {t('admin.users.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('admin.users.subtitle')}
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<PersonAdd />}
                    onClick={() => setCreateOpen(true)}
                    sx={{ flexShrink: 0 }}
                >
                    {t('admin.users.createUser')}
                </Button>
            </Box>

            <TextField
                fullWidth
                size="small"
                placeholder={t('admin.users.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ mb: 3 }}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <Search color="action" />
                            </InputAdornment>
                        ),
                    },
                }}
            />

            <Card
                sx={{
                    border: '1px solid',
                    borderColor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                }}
            >
                <CardContent sx={{ p: 0 }}>
                    {loading ? (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Typography color="text.secondary">{t('common.loading')}</Typography>
                        </Box>
                    ) : filteredUsers.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <Typography color="text.secondary">{t('common.noData')}</Typography>
                        </Box>
                    ) : (
                        <>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell width={40} />
                                            <TableCell>{t('admin.users.name')}</TableCell>
                                            <TableCell>{t('admin.users.email')}</TableCell>
                                            <TableCell>{t('admin.users.domain')}</TableCell>
                                            <TableCell>{t('admin.users.role')}</TableCell>
                                            <TableCell>{t('admin.users.status')}</TableCell>
                                            <TableCell>{t('admin.users.plans')}</TableCell>
                                            <TableCell>{t('admin.users.apiKeys')}</TableCell>
                                            <TableCell>{t('admin.users.createdAt')}</TableCell>
                                            <TableCell align="right">{t('admin.users.actions')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {paginatedUsers.map((user) => (
                                            <UserRow
                                                key={user.id}
                                                user={user}
                                                expanded={expandedUserId === user.id}
                                                onToggleExpand={() =>
                                                    setExpandedUserId(expandedUserId === user.id ? null : user.id)
                                                }
                                                onToggleRole={() => setConfirmAction({ type: 'role', user })}
                                                onToggleStatus={() => setConfirmAction({ type: 'status', user })}
                                                onGrantPlan={(planId) => setConfirmAction({ type: 'grant-plan', user, planId })}
                                                onRevokePlan={(planId) => setConfirmAction({ type: 'revoke-plan', user, planId })}
                                                onResetPassword={() => {
                                                    setPasswordUser(user);
                                                    setNewPassword('');
                                                    setShowResetPassword(false);
                                                }}
                                                formatDate={formatDate}
                                                getActivePlans={getActivePlans}
                                                allowedDomains={allowedDomains}
                                                t={t}
                                            />
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={filteredUsers.length}
                                page={page}
                                onPageChange={(_e, newPage) => setPage(newPage)}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={(e) => {
                                    setRowsPerPage(Number.parseInt(e.target.value, 10));
                                    setPage(0);
                                }}
                                rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                                labelRowsPerPage={t('admin.users.rowsPerPage')}
                            />
                        </>
                    )}
                </CardContent>
            </Card>

            <Dialog
                open={!!confirmAction}
                onClose={() => !actionLoading && setConfirmAction(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 600 }}>{t('common.confirm')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{getConfirmMessage()}</DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setConfirmAction(null)} disabled={actionLoading}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        color={
                            (confirmAction?.type === 'status' && confirmAction.user.status === 'active') ||
                            confirmAction?.type === 'revoke-plan'
                                ? 'error'
                                : 'primary'
                        }
                        onClick={handleConfirm}
                        disabled={actionLoading}
                    >
                        {actionLoading ? t('common.loading') : t('common.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={createOpen}
                onClose={() => !createLoading && setCreateOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 600 }}>{t('admin.users.createUser')}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <TextField
                        fullWidth
                        label={t('admin.users.name')}
                        value={createForm.name}
                        onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
                        required
                        margin="dense"
                    />
                    <TextField
                        fullWidth
                        label={t('admin.users.email')}
                        type="email"
                        value={createForm.email}
                        onChange={(e) => setCreateForm((f) => ({ ...f, email: e.target.value }))}
                        required
                    />
                    <TextField
                        fullWidth
                        label={t('admin.users.password')}
                        type={showCreatePassword ? 'text' : 'password'}
                        value={createForm.password}
                        onChange={(e) => setCreateForm((f) => ({ ...f, password: e.target.value }))}
                        required
                        error={!!createForm.password && createForm.password.length < 8}
                        helperText={
                            createForm.password && createForm.password.length < 8
                                ? t('errors.weakPassword')
                                : ''
                        }
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowCreatePassword(!showCreatePassword)}
                                            edge="end"
                                            size="small"
                                        >
                                            {showCreatePassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                    <Autocomplete
                        options={COUNTRIES}
                        getOptionLabel={(option) => option.name}
                        value={createForm.country}
                        onChange={(_e, value) => setCreateForm((f) => ({ ...f, country: value }))}
                        filterOptions={(options, { inputValue }) => {
                            const q = inputValue.toLowerCase();
                            return options.filter(
                                (o) => o.name.toLowerCase().includes(q) || o.nameEn.toLowerCase().includes(q),
                            );
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label={t('auth.register.country')} required />
                        )}
                    />
                    <FormControl fullWidth>
                        <InputLabel id="create-user-role-label">{t('admin.users.role')}</InputLabel>
                        <Select
                            labelId="create-user-role-label"
                            label={t('admin.users.role')}
                            value={createForm.role}
                            onChange={(e) =>
                                setCreateForm((f) => ({ ...f, role: e.target.value as 'user' | 'admin' }))
                            }
                        >
                            <MenuItem value="user">{t('admin.users.roleUser')}</MenuItem>
                            <MenuItem value="admin">{t('admin.users.roleAdmin')}</MenuItem>
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setCreateOpen(false)} disabled={createLoading}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleCreateUser}
                        disabled={createLoading || !isCreateValid}
                    >
                        {createLoading ? t('common.loading') : t('admin.users.createUser')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={!!passwordUser}
                onClose={() => !passwordLoading && setPasswordUser(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 600 }}>{t('admin.users.resetPassword')}</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        {t('admin.users.resetPasswordFor', { user: passwordUser?.name ?? '' })}
                    </DialogContentText>
                    <TextField
                        fullWidth
                        label={t('admin.users.newPassword')}
                        type={showResetPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        error={!!newPassword && newPassword.length < 8}
                        helperText={
                            newPassword && newPassword.length < 8 ? t('errors.weakPassword') : ''
                        }
                        slotProps={{
                            input: {
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowResetPassword(!showResetPassword)}
                                            edge="end"
                                            size="small"
                                        >
                                            {showResetPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setPasswordUser(null)} disabled={passwordLoading}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleResetPassword}
                        disabled={passwordLoading || newPassword.length < 8}
                    >
                        {passwordLoading ? t('common.loading') : t('common.save')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}

interface UserRowProps {
    user: AdminUserView;
    expanded: boolean;
    onToggleExpand: () => void;
    onToggleRole: () => void;
    onToggleStatus: () => void;
    onGrantPlan: (planId: string) => void;
    onRevokePlan: (planId: string) => void;
    onResetPassword: () => void;
    formatDate: (date: string) => string;
    getActivePlans: (user: AdminUserView) => string;
    allowedDomains: Set<string>;
    t: (key: string, params?: Record<string, string>) => string;
}

function UserRow({
    user,
    expanded,
    onToggleExpand,
    onToggleRole,
    onToggleStatus,
    onGrantPlan,
    onRevokePlan,
    onResetPassword,
    formatDate,
    getActivePlans,
    allowedDomains,
    t,
}: UserRowProps) {
    const userDomain = extractDomain(user.email);
    const isWhitelisted = allowedDomains.has(userDomain);

    return (
        <>
            <TableRow sx={{ '& > *': { borderBottom: expanded ? 'unset' : undefined } }}>
                <TableCell>
                    <IconButton size="small" onClick={onToggleExpand}>
                        {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                    </IconButton>
                </TableCell>
                <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {user.name}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Typography variant="body2" color="text.secondary">
                        {user.email}
                    </Typography>
                </TableCell>
                <TableCell>
                    <Chip
                        label={isWhitelisted ? t('admin.users.whitelisted') : t('admin.users.waitlisted')}
                        size="small"
                        color={isWhitelisted ? 'success' : 'default'}
                        variant="outlined"
                    />
                </TableCell>
                <TableCell>
                    <Chip
                        icon={user.role === 'admin' ? <Shield fontSize="small" /> : <Person fontSize="small" />}
                        label={user.role === 'admin' ? t('admin.users.roleAdmin') : t('admin.users.roleUser')}
                        size="small"
                        color={user.role === 'admin' ? 'warning' : 'default'}
                        variant="outlined"
                    />
                </TableCell>
                <TableCell>
                    <Chip
                        label={user.status === 'active' ? t('admin.users.statusActive') : t('admin.users.statusDisabled')}
                        size="small"
                        color={user.status === 'active' ? 'success' : 'error'}
                        variant="outlined"
                    />
                </TableCell>
                <TableCell>
                    <Typography variant="body2">{getActivePlans(user)}</Typography>
                </TableCell>
                <TableCell>
                    <Typography variant="body2">{user.apiKeysCount}</Typography>
                </TableCell>
                <TableCell>
                    <Typography variant="body2" color="text.secondary">
                        {formatDate(user.createdAt)}
                    </Typography>
                </TableCell>
                <TableCell align="right">
                    {PLAN_IDS.map((pid) => {
                        const activeSub = user.subscriptions.find(
                            (s) => s.planId === pid && s.status === 'active',
                        );
                        if (activeSub?.adminGranted) {
                            return (
                                <Tooltip key={pid} title={`${t('admin.users.revokePlan')} ${planLabel(pid)}`}>
                                    <IconButton size="small" onClick={() => onRevokePlan(pid)} color="error">
                                        <RemoveCircleOutline fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            );
                        }
                        if (!activeSub) {
                            return (
                                <Tooltip key={pid} title={`${t('admin.users.grantPlan')} ${planLabel(pid)}`}>
                                    <IconButton size="small" onClick={() => onGrantPlan(pid)} color="success">
                                        <AddCircleOutline fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            );
                        }
                        return null;
                    })}
                    <Tooltip title={t('admin.users.resetPassword')}>
                        <IconButton size="small" onClick={onResetPassword} color="primary">
                            <LockReset fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip
                        title={
                            user.role === 'admin'
                                ? t('admin.users.demoteUser')
                                : t('admin.users.promoteAdmin')
                        }
                    >
                        <IconButton size="small" onClick={onToggleRole} color="warning">
                            <AdminPanelSettings fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip
                        title={
                            user.status === 'active'
                                ? t('admin.users.disableAccount')
                                : t('admin.users.enableAccount')
                        }
                    >
                        <IconButton
                            size="small"
                            onClick={onToggleStatus}
                            color={user.status === 'active' ? 'error' : 'success'}
                        >
                            {user.status === 'active' ? (
                                <Block fontSize="small" />
                            ) : (
                                <CheckCircle fontSize="small" />
                            )}
                        </IconButton>
                    </Tooltip>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell sx={{ py: 0 }} colSpan={10}>
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, px: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                                <Storage fontSize="small" color="action" />
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    {t('admin.users.sapEnvironments')}
                                </Typography>
                            </Box>
                            {user.sapEnvironments.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ pl: 3.5 }}>
                                    {t('admin.users.noSapEnvironments')}
                                </Typography>
                            ) : (
                                <Table size="small" sx={{ ml: 3.5 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{t('admin.users.identifier')}</TableCell>
                                            <TableCell>{t('admin.users.description')}</TableCell>
                                            <TableCell>{t('admin.users.host')}</TableCell>
                                            <TableCell>{t('admin.users.client')}</TableCell>
                                            <TableCell>{t('admin.users.username')}</TableCell>
                                            <TableCell>{t('admin.users.language')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {user.sapEnvironments.map((env) => (
                                            <TableRow key={env.id}>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontFamily: '"JetBrains Mono", monospace',
                                                            fontSize: '0.75rem',
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {env.identifier}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{env.description}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            fontFamily: '"JetBrains Mono", monospace',
                                                            fontSize: '0.75rem',
                                                        }}
                                                    >
                                                        {env.host}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{env.client}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{env.username}</Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">{env.language}</Typography>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
}

export default AdminUsersPage;
